import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import '../styles/test-system.css';
import './TestCycle.css';
import Navigation from '../components/Navigation';
import Countdown, { useNow } from '../components/tests/Countdown';
import ConfirmationDeadline from '../components/tests/ConfirmationDeadline';
import CycleStatusChip from '../components/tests/CycleStatusChip';
import EntryPackPicker, { useEntryPacks } from '../components/tests/EntryPackPicker';
import {
    enrolWithEntry,
    getMyEnrolments,
    getMyEntries,
    getTestCycle,
    readApiError,
    startCycleCheckout,
} from '../api/testSystem';
import { entriesLabel } from '../lib/entryPacks';
import { canOpenTrial, trialHref } from '../lib/trialDoor';
import { isAuthenticated } from '../utils/auth';
import { formatInTimeZone, getTimeZoneLabel, parseUTCToLocal } from '../utils/dateUtils';
import {
    formatMinutes,
    formatSpots,
    formatTrialFormat,
    formatZoneName,
    getWindowOpensAt,
    isCycleFull,
    isEnrolmentOpen,
} from '../utils/testCycle';

const InfoIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
);

const AlertIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
);

const CheckIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

/* What a candidate is told about their own enrolment, per its status. */
const ENROLMENT_NOTES = {
    enrolled: "You're enrolled on this cycle.",
    confirmed: 'Your participation is confirmed.',
    no_show: 'You did not confirm in time, so your entry was returned to your balance.',
    cancelled_credited: 'This cycle was cancelled and your entry was returned to your balance.',
};

/* What is happening to a cycle a candidate can no longer enrol on. */
const STATUS_NOTES = {
    confirmation: 'Enrolment has closed. Enrolled candidates are confirming their spot — the cycle starts when the confirmation window closes.',
    running: 'This cycle is running.',
    ended: 'This cycle has ended.',
    cancelled: 'This cycle was cancelled.',
};

const isNotFound = (error) => error?.response?.status === 404;

const TestCycle = () => {
    const { cycleId } = useParams();
    const navigate = useNavigate();
    const now = useNow();

    const [cycle, setCycle] = useState(null);
    const [enrolment, setEnrolment] = useState(null);
    const [balance, setBalance] = useState(0);
    const [loading, setLoading] = useState(true);
    // '' while the page has a cycle, 'unavailable' for a 404, 'failed' for anything else.
    const [loadError, setLoadError] = useState('');
    const [pendingAction, setPendingAction] = useState('');
    const [actionError, setActionError] = useState('');
    const [justEnrolled, setJustEnrolled] = useState(false);
    // Prices are only worth reading once there is an open cycle to buy a place on.
    const { loading: packsLoading, packs } = useEntryPacks(cycle?.status === 'open');
    const [selectedPack, setSelectedPack] = useState('');
    const [packsOpen, setPacksOpen] = useState(false);
    // One entry is picked until the student picks another pack.
    const chosenPack = packs.find(pack => pack.productId === selectedPack) || packs[0];

    const loadCycle = useCallback(async () => {
        setLoading(true);
        setLoadError('');

        try {
            const data = await getTestCycle(cycleId);
            if (!data) {
                setLoadError('unavailable');
                return;
            }
            setCycle(data);
        } catch (e) {
            // A gated cycle, or one past enrolment that the candidate holds no place on,
            // answers 404 exactly like an unknown id, and this page treats them all the same.
            setLoadError(isNotFound(e) ? 'unavailable' : 'failed');
        } finally {
            setLoading(false);
        }
    }, [cycleId]);

    // The balance and the candidate's own enrolment only shape the CTAs — the
    // page is still worth showing if either read fails.
    const loadCandidateState = useCallback(async () => {
        try {
            const entries = await getMyEntries();
            setBalance(entries?.entries_balance ?? 0);
        } catch (e) {
            console.error('Error loading entries balance:', e);
        }

        try {
            const rows = await getMyEnrolments();
            setEnrolment(rows.find(row => row.cycle?.cycle_id === cycleId)?.enrolment || null);
        } catch (e) {
            console.error('Error loading enrolments:', e);
        }
    }, [cycleId]);

    useEffect(() => {
        if (!isAuthenticated()) {
            navigate('/login');
            return;
        }

        loadCycle();
        loadCandidateState();
    }, [navigate, loadCycle, loadCandidateState]);

    const handleUseEntry = async () => {
        setPendingAction('enrol');
        setActionError('');

        try {
            const data = await enrolWithEntry(cycleId);
            setEnrolment(data?.enrolment || null);
            if (typeof data?.entries_balance === 'number') setBalance(data.entries_balance);
            setJustEnrolled(true);

            const fresh = await getTestCycle(cycleId).catch(() => null);
            if (fresh) setCycle(fresh);
        } catch (e) {
            if (isNotFound(e)) {
                // The cycle closed enrolment under the page and the candidate is not on it.
                setLoadError('unavailable');
            } else {
                setActionError(readApiError(e, 'You could not be enrolled on this cycle.').message);
            }
        }

        setPendingAction('');
    };

    const handleCheckout = async (productId) => {
        setPendingAction('checkout');
        setActionError('');

        try {
            const data = await startCycleCheckout(cycleId, productId);
            if (data?.checkout_url) {
                // Stripe's page shows the chosen pack and cannot switch it; the spot
                // is held until the session it just opened expires. Stay busy
                // through the redirect.
                window.location.href = data.checkout_url;
                return;
            }
            setActionError('Checkout could not be started. Please try again.');
        } catch (e) {
            if (isNotFound(e)) {
                setLoadError('unavailable');
            } else {
                setActionError(readApiError(e, 'Checkout could not be started.').message);
            }
        }

        setPendingAction('');
    };

    const timeZone = cycle?.display_timezone;

    /* Each row carries both tenses: a moment that has gone is labelled as having
       gone, rather than promising something in the future beside a greyed-out
       "closed". A manual-start cohort has no `start_at`/`ends_at` until the team
       starts it, and then the moments it actually ran are the ones to show. */
    const schedule = useMemo(() => {
        if (!cycle) return [];

        return [
            { key: 'cutoff', label: 'Enrolment closes', past: 'Enrolment closed', at: cycle.enrolment_cutoff_at },
            { key: 'window', label: 'Confirmation window opens', past: 'Confirmation window opened', at: getWindowOpensAt(cycle) },
            { key: 'start', label: 'Cycle starts', past: 'Cycle started', at: cycle.start_at || cycle.started_at },
            { key: 'ends', label: 'Cycle ends', past: 'Cycle ended', at: cycle.ends_at || cycle.ended_at },
        ].filter(row => row.at);
    }, [cycle]);

    if (loading) {
        return (
            <>
                <Navigation />
                <div className="ts-page">
                    <div className="ts-shell ts-shell--wide">
                        <div className="ts-page-state ts-page-state--loading" data-testid="cycle-loading">
                            <div className="ts-spinner" />
                            <p>Loading this test…</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (loadError === 'failed') {
        return (
            <>
                <Navigation />
                <div className="ts-page">
                    <div className="ts-shell ts-shell--wide">
                        <div className="ts-page-state" role="alert" data-testid="cycle-load-failed">
                            <h2>This test could not be loaded</h2>
                            <p>Something went wrong on our side. Please try again in a moment.</p>
                            <button className="ts-btn" onClick={loadCycle}>Try again</button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (loadError || !cycle) {
        return (
            <>
                <Navigation />
                <div className="ts-page">
                    <div className="ts-shell ts-shell--wide">
                        <div className="ts-page-state" data-testid="cycle-unavailable">
                            <h2>This test isn't available to you</h2>
                            <p>It may have closed, or it's only open to the students taking part.</p>
                            <Link className="ts-btn" to="/tests">Back to the catalog</Link>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    const enrolmentOpen = isEnrolmentOpen(cycle, now);
    const zoneName = formatZoneName(timeZone);
    const full = isCycleFull(cycle);
    const holdsSpot = enrolment && ['enrolled', 'confirmed'].includes(enrolment.status);
    // An enrolment document of any status means the backend refuses a second one,
    // so its mere presence, not just an active spot, takes the CTAs away.
    const canAct = cycle.status === 'open' && !enrolment;
    const awaitingConfirmation = cycle.status === 'confirmation' && enrolment?.status === 'enrolled';
    const hasEntries = balance > 0;
    const canEnrolNow = enrolmentOpen && !full;
    /* Buying is on the page only once the server has said there is something to
       buy. While that is unknown, and when the answer is no — nothing on sale, or
       a packs route that failed — the page says nothing about buying at all. */
    const canBuy = !packsLoading && Boolean(chosenPack);
    // With an entry to spend, buying is the second choice and waits behind a button.
    const showPacks = canBuy && canEnrolNow && (!hasEntries || packsOpen);
    // No entry, and none to be had here: say what enrolling takes, and nothing else.
    const needsEntry = canEnrolNow && !hasEntries && !packsLoading && !canBuy;

    const blockedReason = full
        ? 'Every spot on this cycle is taken.'
        : cycle.enrolment_cutoff_at
            ? `Enrolment closed on ${formatInTimeZone(cycle.enrolment_cutoff_at, timeZone)} ${getTimeZoneLabel(cycle.enrolment_cutoff_at, timeZone)}.`
            : 'Enrolment for this cycle is closed.';

    return (
        <>
            <Navigation />
            <div className="ts-page">
                <div className="ts-shell ts-shell--wide">
                    <button className="ts-back" onClick={() => navigate('/tests')}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M15 18l-6-6 6-6" />
                        </svg>
                        All tests
                    </button>

                    <header className="ts-head">
                        <div className="ts-head-main">
                            {/* What it is and where it stands, on one line over its name. */}
                            <div className="ts-cycle-kicker">
                                <span className="ts-eyebrow">Test cycle</span>
                                <CycleStatusChip status={cycle.status} enrolmentOpen={enrolmentOpen} full={full} />
                            </div>
                            <h1 className="ts-title">{cycle.structure_name || 'Test'}</h1>
                            {cycle.structure_description && (
                                <p className="ts-lede">{cycle.structure_description}</p>
                            )}
                        </div>
                    </header>

                    <div className="ts-cycle-grid">
                        <div className="ts-cycle-main">
                            <section className="ts-panel">
                                <h2 className="ts-panel-title">At a glance</h2>
                                <dl className="ts-facts">
                                    <div className="ts-fact">
                                        <dt>Duration</dt>
                                        <dd>{formatMinutes(cycle.version?.duration)}</dd>
                                    </div>
                                    <div className="ts-fact">
                                        <dt>Format</dt>
                                        <dd>{formatTrialFormat(cycle.version)}</dd>
                                    </div>
                                    {/* Spots left only mean something while the cohort can
                                        still be joined. */}
                                    {enrolmentOpen && (
                                        <div className="ts-fact">
                                            <dt>Spots</dt>
                                            <dd data-testid="cycle-spots">{formatSpots(cycle)}</dd>
                                        </div>
                                    )}
                                    <div className="ts-fact">
                                        <dt>Times shown in</dt>
                                        <dd>
                                            {getTimeZoneLabel(cycle.start_at, timeZone) || 'your local time'}
                                            {zoneName && <span className="ts-fact-sub">{zoneName}</span>}
                                        </dd>
                                    </div>
                                </dl>
                            </section>

                            <section className="ts-panel">
                                <h2 className="ts-panel-title">Schedule</h2>
                                {schedule.length === 0 ? (
                                    <p className="ts-cycle-manual">
                                        This cycle has no scheduled start yet — the team starts it manually
                                        and everyone enrolled is emailed when it does.
                                    </p>
                                ) : (
                                    <dl className="ts-schedule" data-testid="cycle-schedule">
                                        {schedule.map((row) => {
                                            const passed = (parseUTCToLocal(row.at)?.getTime() ?? 0) <= now;

                                            return (
                                                <div className="ts-schedule-row" key={row.key}>
                                                    <dt>{passed ? row.past : row.label}</dt>
                                                    <dd>
                                                        <span className="ts-schedule-time">
                                                            {formatInTimeZone(row.at, timeZone)}{' '}
                                                            <span className="ts-schedule-zone">
                                                                {getTimeZoneLabel(row.at, timeZone)}
                                                            </span>
                                                        </span>
                                                        {!passed && <Countdown target={row.at} />}
                                                    </dd>
                                                </div>
                                            );
                                        })}
                                    </dl>
                                )}
                            </section>
                        </div>

                        <aside className="ts-cycle-aside">
                            <section className="ts-panel ts-action-panel" data-testid="cycle-actions">
                                <h2 className="ts-panel-title">Your enrolment</h2>

                                {justEnrolled && (
                                    <p className="ts-note ts-note--ok" role="status">
                                        <CheckIcon />
                                        <span>You're enrolled. We'll email you when the confirmation window opens.</span>
                                    </p>
                                )}

                                {actionError && (
                                    <p className="ts-note ts-note--danger" role="alert" data-testid="cycle-action-error">
                                        <AlertIcon />
                                        <span>{actionError}</span>
                                    </p>
                                )}

                                {holdsSpot ? (
                                    <>
                                        <p className="ts-note ts-note--info">
                                            <InfoIcon />
                                            <span>{ENROLMENT_NOTES[enrolment.status]}</span>
                                        </p>
                                        {awaitingConfirmation && (
                                            <>
                                                <ConfirmationDeadline cycle={cycle} />
                                                <Link className="ts-btn ts-btn--primary ts-action-btn" to="/tests/enrolments">
                                                    Confirm participation
                                                </Link>
                                            </>
                                        )}
                                        {canOpenTrial(enrolment, cycle) && (
                                            <Link
                                                className="ts-btn ts-btn--primary ts-action-btn"
                                                to={trialHref({ work_trial_id: enrolment.work_trial_id })}
                                            >
                                                Open your work trial
                                            </Link>
                                        )}
                                    </>
                                ) : enrolment ? (
                                    <p className="ts-note ts-note--info">
                                        <InfoIcon />
                                        <span>{ENROLMENT_NOTES[enrolment.status]}</span>
                                    </p>
                                ) : null}

                                {canAct ? (
                                    <>
                                        <p className="ts-action-balance">
                                            You have <strong>{balance}</strong> {balance === 1 ? 'entry' : 'entries'}
                                        </p>

                                        {!enrolmentOpen || full ? (
                                            <p className="ts-note ts-note--warn" data-testid="cycle-blocked">
                                                <AlertIcon />
                                                <span>{blockedReason}</span>
                                            </p>
                                        ) : null}

                                        {hasEntries && (
                                            <button
                                                className="ts-btn ts-btn--primary ts-action-btn"
                                                onClick={handleUseEntry}
                                                disabled={!canEnrolNow || !!pendingAction}
                                                data-testid="use-entry"
                                            >
                                                {pendingAction === 'enrol' && <span className="ts-spinner-sm" />}
                                                Enrol — uses 1 entry
                                            </button>
                                        )}

                                        {canBuy && canEnrolNow && hasEntries && !packsOpen && (
                                            <button
                                                className="ts-btn ts-action-btn ts-action-btn--after"
                                                onClick={() => setPacksOpen(true)}
                                                disabled={!!pendingAction}
                                                aria-expanded="false"
                                                data-testid="show-packs"
                                            >
                                                Buy entries and enrol
                                            </button>
                                        )}

                                        {showPacks && (
                                            <div
                                                className={`ts-action-buy${hasEntries ? ' ts-action-buy--second' : ''}`}
                                                data-testid="enrol-packs"
                                            >
                                                <p className="ts-action-hint" data-testid="enrol-hint">
                                                    One entry enrols you here and any extra stay in your
                                                    balance. Your spot is held while you pay.
                                                </p>
                                                <EntryPackPicker
                                                    packs={packs}
                                                    selected={chosenPack.productId}
                                                    onSelect={setSelectedPack}
                                                    disabled={!!pendingAction}
                                                    name="enrol-pack"
                                                    label="Entry packs"
                                                    layout="stack"
                                                    autoFocus={packsOpen}
                                                />
                                                <button
                                                    className={`ts-btn ts-action-btn ts-action-btn--after${hasEntries ? '' : ' ts-btn--primary'}`}
                                                    onClick={() => handleCheckout(chosenPack.productId)}
                                                    disabled={!!pendingAction}
                                                    data-testid="enrol-now"
                                                >
                                                    {pendingAction === 'checkout' && <span className="ts-spinner-sm" />}
                                                    Buy {entriesLabel(chosenPack.entries)} and enrol
                                                </button>
                                            </div>
                                        )}

                                        {needsEntry && (
                                            <p className="ts-action-hint ts-action-hint--last" data-testid="needs-entry">
                                                Enrolling on a cycle uses one entry.
                                            </p>
                                        )}

                                        {canBuy && (
                                            <p className="ts-action-aside">
                                                <Link className="ts-link" to="/tests/entries">Buy entries</Link>
                                            </p>
                                        )}
                                    </>
                                ) : cycle.status !== 'open' && !awaitingConfirmation ? (
                                    /* Never `cancelled_reason`: it is an operator's note to
                                       the team, not a sentence written for a candidate. The
                                       confirm CTA carries its own, sharper note, so this
                                       general one stands down while that is up. */
                                    <p className="ts-action-status" data-testid="cycle-status-view">
                                        {STATUS_NOTES[cycle.status] || 'Enrolment for this cycle is closed.'}
                                    </p>
                                ) : null}
                            </section>
                        </aside>
                    </div>
                </div>
            </div>
        </>
    );
};

export default TestCycle;
